import { MessageSquare, Phone, Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { WHATSAPP_LINK, TEL_LINK } from "@/lib/site";

const whatsappFeatures = [
    "Atende mensagens e entende o contexto da conversa",
    "Ouve e interpreta áudios do WhatsApp — o cliente pode mandar mensagem de voz normalmente",
    "Agenda consultas e reuniões direto na agenda do profissional",
    "Envia confirmação pro cliente e notifica o profissional",
    "Lembra o cliente 24h antes e 1h antes (ou como preferir) — personalizável com o seu negócio",
    "Remarca e cancela quando necessário",
];

const voiceFeatures = [
    "Atende ligações que chegam no número do negócio",
    "Coleta nome, motivo e disponibilidade do cliente",
    "Agenda direto na agenda do profissional",
    "Envia resumo da ligação por WhatsApp pro profissional",
    "Lembra o cliente por WhatsApp antes da consulta (24h e 1h antes, ou como preferir)",
    "Remarca e cancela quando necessário",
];

const VoiceWave = () => (
    <div className="flex items-center justify-center gap-[3px] h-16 rounded-xl border border-[#1E1E28] bg-[#0A0A0F] px-4">
        {[0.4, 0.8, 1, 0.6, 0.9, 0.3, 0.7, 1, 0.5, 0.85, 0.4, 0.95, 0.6, 0.8, 0.45].map(
            (h, i) => (
                <span
                    key={i}
                    className="w-1 rounded-full bg-gradient-to-t from-[#3B6FFF] to-[#94B4FF]"
                    style={{
                        height: `${h * 100}%`,
                        animation: "dg-wave 1.2s ease-in-out infinite",
                        animationDelay: `${i * 0.07}s`,
                    }}
                />
            )
        )}
    </div>
);

const ChatBubbles = () => (
    <div className="space-y-2.5 rounded-xl border border-[#1E1E28] bg-[#0A0A0F] p-4">
        <div className="max-w-[75%] rounded-2xl rounded-bl-sm bg-[#1E1E28] px-3.5 py-2 text-xs text-[#F5F5F5]/80">
            Quanto custa a primeira sessão?
        </div>
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-[#3B6FFF] px-3.5 py-2 text-xs text-white">
            A avaliação é R$ 500. Quer que eu já reserve um horário pra você?
        </div>
    </div>
);

const ProductCard = ({ testid, icon: Icon, tag, title, where, diff, features, visual, cta }) => (
    <div
        data-testid={testid}
        className="relative flex flex-col h-full rounded-3xl border border-[#1E1E28] bg-[#121218] p-8 md:p-10 overflow-hidden transition-colors hover:border-[#3B6FFF]/40"
    >
        <div className="absolute -top-24 -right-24 h-60 w-60 dg-glow opacity-60" />
        <div className="relative">
            <div className="flex items-center gap-3">
                <div className="inline-flex items-center justify-center rounded-xl bg-[#3B6FFF]/15 p-3 text-[#3B6FFF]">
                    <Icon className="w-6 h-6" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-[#94B4FF]">
                    {tag}
                </span>
            </div>
            <h3 className="mt-6 font-display text-2xl font-bold text-[#F5F5F5]">
                {title}
            </h3>

            <div className="mt-6">{visual}</div>

            <ul className="mt-7 space-y-3">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[#F5F5F5]/80">
                        <Check className="mt-0.5 w-4 h-4 flex-shrink-0 text-[#3B6FFF]" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-7 space-y-3 border-t border-[#1E1E28] pt-6">
                <p className="text-sm text-[#F5F5F5]/70">
                    <span className="text-[#94B4FF] font-semibold">Onde funciona: </span>
                    {where}
                </p>
                <p className="text-sm text-[#F5F5F5]/70">
                    <span className="text-[#94B4FF] font-semibold">Diferencial: </span>
                    {diff}
                </p>
            </div>

            {cta}
        </div>
    </div>
);

export const Products = () => {
    return (
        <section id="produtos" className="relative py-24 md:py-32 lg:py-40">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <Reveal>
                    <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-[#94B4FF]">
                        Os dois produtos
                    </p>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F5F5F5] max-w-3xl">
                        Um agente no WhatsApp. Outro na ligação. Os dois trabalhando por você.
                    </h2>
                </Reveal>

                <div className="mt-14 grid lg:grid-cols-2 gap-6 md:gap-8">
                    <Reveal>
                        <ProductCard
                            testid="product-whatsapp"
                            icon={MessageSquare}
                            tag="Agente de WhatsApp"
                            title="Atende e agenda direto no chat"
                            where="no WhatsApp do próprio negócio — o cliente não percebe que é IA."
                            diff="responde como humano, não trava com perguntas inesperadas, totalmente personalizado por nicho."
                            features={whatsappFeatures}
                            visual={<ChatBubbles />}
                            cta={
                                <div className="mt-7">
                                    <a
                                        data-testid="product-whatsapp-cta"
                                        href={WHATSAPP_LINK}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#3B6FFF] px-6 py-3 font-semibold text-white shadow-[0_0_25px_rgba(59,111,255,0.35)] hover:bg-[#2d5be0] transition-all"
                                    >
                                        <WhatsAppIcon className="w-5 h-5" />
                                        Testar agora no WhatsApp
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </a>
                                    <p className="mt-3 text-xs text-[#7A7A90]">
                                        Mande uma mensagem e agende uma conversa comigo. O
                                        agente responde agora.
                                    </p>
                                </div>
                            }
                        />
                    </Reveal>
                    <Reveal delay={0.12}>
                        <ProductCard
                            testid="product-voice"
                            icon={Phone}
                            tag="Agente de Ligação"
                            title="Atende o telefone e marca tudo"
                            where="no número de telefone atual do negócio (via redirecionamento simples)."
                            diff="voz natural em português brasileiro, difícil de identificar como IA, disponível 24/7."
                            features={voiceFeatures}
                            visual={<VoiceWave />}
                            cta={
                                <div className="mt-7">
                                    <a
                                        data-testid="product-voice-cta"
                                        href={TEL_LINK}
                                        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#3B6FFF] px-6 py-3 font-semibold text-white shadow-[0_0_25px_rgba(59,111,255,0.35)] hover:bg-[#2d5be0] transition-all"
                                    >
                                        <Phone className="w-5 h-5" />
                                        Ligar e testar ao vivo
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </a>
                                    <p className="mt-3 text-xs text-[#7A7A90]">
                                        Ligue agora e passe pelo fluxo completo. Teste ao
                                        vivo.
                                    </p>
                                </div>
                            }
                        />
                    </Reveal>
                </div>

                <Reveal delay={0.2}>
                    <div className="mt-10 rounded-2xl border border-[#3B6FFF]/30 bg-[#3B6FFF]/5 px-6 py-5 text-center">
                        <p className="font-display text-lg md:text-xl font-semibold text-[#F5F5F5]">
                            Os dois produtos funcionam de forma independente — e podem ser
                            combinados.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Products;
